import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  createTeam,
  updateTeam,
  archiveTeam,
} from "../../redux/teams/actions";
import { UseTeams, UseTeamCounts } from "../../hooks/teams";

/**
 * Team list for the Players table — backend-backed.
 *
 * Public contract is intentionally identical to the old localStorage shim
 * so call sites in `UmTable` and `CustomizeViewModal` did not need to change
 * their destructuring:
 *
 *   const { teams, counts, create, rename, remove } = useTeams(users);
 *
 * `users` is still accepted for backward compatibility but is no longer used
 * for deriving the list — the canonical list comes from Redux, which itself
 * comes from `GET /team`.
 */
// eslint-disable-next-line no-unused-vars
export function useTeams(_users = []) {
  const dispatch = useDispatch();
  const teamsList = UseTeams();
  const reduxCounts = UseTeamCounts();

  // Old shape: `teams` is an array of names (strings), `counts` keyed by name.
  // Keep that shape so consumers stay unchanged.
  const teams = useMemo(() => teamsList.map((t) => t.name), [teamsList]);
  const counts = reduxCounts;

  const findByName = useCallback(
    (name) => teamsList.find((t) => t.name === name),
    [teamsList],
  );

  const create = useCallback(
    (rawName) => {
      const name = String(rawName || "").trim();
      if (!name) return false;
      if (teams.includes(name)) return false;
      dispatch(createTeam({ name }));
      return true;
    },
    [dispatch, teams],
  );

  const rename = useCallback(
    (oldName, newRaw) => {
      const newName = String(newRaw || "").trim();
      if (!newName || newName === oldName) return false;
      const existing = findByName(oldName);
      if (!existing) return false;
      dispatch(updateTeam(existing._id, { name: newName }));
      return true;
    },
    [dispatch, findByName],
  );

  const remove = useCallback(
    (name) => {
      const existing = findByName(name);
      if (!existing) return false;
      dispatch(archiveTeam(existing._id));
      return true;
    },
    [dispatch, findByName],
  );

  return { teams, counts, create, rename, remove };
}
